// CustomObjectFieldTabs.tsx
import React from "react"
import { Alert, Box, LinearProgress, Tab, Tabs, Typography } from "@mui/material"
import { LocationProvider } from "../../LocationSelector"
import { ObjectFieldTemplateProps, RJSFSchema, FormContextType } from "@rjsf/utils"
import { isGoalRaffleFormData } from "./raffleFormUtils"
import TicketPreview from "../TicketPreview"

/**
 * Utility: resolved (post-conditional) schema visibility check.
 * If a property name exists in resolvedSchema.properties, it's "active" and should render.
 */
function useResolvedSchemaVisibility<T = any, F extends FormContextType = any>(
  schema: RJSFSchema,
  formData: any,
  registry: ObjectFieldTemplateProps<T, F>["registry"],
) {
  const { resolvedSchema, error } = React.useMemo(() => {
    try {
      return {
        resolvedSchema: registry.schemaUtils.retrieveSchema(schema as any, formData) as RJSFSchema,
        error: null,
      }
    } catch (err) {
      const fallbackError =
        err instanceof Error ? err : new Error("Failed to resolve schema visibility")
      return { resolvedSchema: schema, error: fallbackError }
    }
  }, [registry.schemaUtils, schema, formData])

  const resolvedProps = React.useMemo(
    () => (resolvedSchema?.properties || {}) as Record<string, RJSFSchema>,
    [resolvedSchema],
  )

  const isVisible = React.useCallback(
    (propName: string) => {
      if (error) return true
      return Object.prototype.hasOwnProperty.call(resolvedProps, propName)
    },
    [resolvedProps, error],
  )

  return { resolvedSchema, isVisible, error }
}

type TabsUiOptions = {
  generalLabel?: string
  emptyStateLabel?: string
  loading?: boolean
  isLoading?: boolean
  loadingLabel?: string
  errorLabel?: string
  tabVariant?: "scrollable" | "standard" | "fullWidth"
}

const DEFAULT_LABELS = {
  general: "General",
  emptyState: "No fields available.",
  loading: "Loading fields...",
  error: "Some fields could not be resolved. Showing all fields.",
} as const

const CONTAINER_SX = {
  width: "100%",
  bgcolor: "background.paper",
  borderRadius: 2,
  border: 1,
  borderColor: "divider",
  overflow: "hidden",
} as const

const HEADER_SX = {
  px: { xs: 2, sm: 3 },
  pt: 2,
  pb: 1,
} as const

const TABS_SX = {
  borderBottom: 1,
  borderColor: "divider",
  px: { xs: 1, sm: 2 },
  "& .MuiTab-root": {
    textTransform: "none",
    fontWeight: 600,
    minHeight: 44,
    px: 2,
  },
  "& .MuiTabs-indicator": {
    height: 3,
    borderRadius: 3,
  },
} as const

const PANEL_SX = {
  px: { xs: 2, sm: 3 },
  py: 2,
} as const

const GROUP_SX = {
  border: 1,
  borderColor: "divider",
  borderRadius: 1.5,
  p: 2,
} as const

const STATUS_SX = {
  px: { xs: 2, sm: 3 },
  pb: 1,
} as const

function LoadingState({ message }: { message: string }) {
  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }} role="status" aria-live="polite">
      <LinearProgress />
      <Typography variant="caption" color="text.secondary">
        {message}
      </Typography>
    </Box>
  )
}

interface TabPanelProps {
  children?: React.ReactNode
  index: number
  value: number
  idBase: string
  className?: string
}

function TabPanel({ children, value, index, idBase, className, ...other }: TabPanelProps) {
  const hidden = value !== index
  return (
    <div
      role="tabpanel"
      hidden={hidden}
      id={`${idBase}-tabpanel-${index}`}
      aria-labelledby={`${idBase}-tab-${index}`}
      className={className}
      {...other}
    >
      {!hidden && (
        <Box sx={PANEL_SX}>
          <Typography component="div">{children}</Typography>
        </Box>
      )}
    </div>
  )
}

/**
 * Custom object field renderer with tabbed grouping.
 *
 * Behavior notes:
 * - "Leaf-like" fields render in the General tab.
 * - Object/array fields render in their own tabs with LocationProvider.
 * - Visibility respects conditional schema resolution via schemaUtils.
 *
 * UI options supported via uiSchema["ui:options"]:
 * - generalLabel: string
 * - emptyStateLabel: string
 * - loading / isLoading: boolean
 * - loadingLabel: string
 * - errorLabel: string
 * - tabVariant: "scrollable" | "standard" | "fullWidth"
 */
const CustomObjectFieldTabs = <T, F extends FormContextType = any>({
  properties,
  title,
  description,
  required,
  formData,
  schema,
  registry,
  uiSchema,
}: ObjectFieldTemplateProps<T, F>) => {
  const [value, setValue] = React.useState(0)
  const idBase = React.useId()
  const onChange = React.useCallback((_e: React.SyntheticEvent, v: number) => setValue(v), [])

  const options = React.useMemo(
    () => ((uiSchema as any)?.["ui:options"] ?? {}) as TabsUiOptions,
    [uiSchema],
  )
  const generalLabel = options.generalLabel ?? DEFAULT_LABELS.general
  const emptyStateLabel = options.emptyStateLabel ?? DEFAULT_LABELS.emptyState
  const loadingLabel = options.loadingLabel ?? DEFAULT_LABELS.loading
  const errorLabel = options.errorLabel ?? DEFAULT_LABELS.error
  const tabVariant = options.tabVariant ?? "scrollable"
  const isLoading = Boolean(options.loading || options.isLoading)

  const { isVisible, error: visibilityError } = useResolvedSchemaVisibility(
    schema as RJSFSchema,
    formData,
    registry,
  )

  // Helper: decide if a property should behave like a "leaf" (stay in General)
  const isLeafLike = React.useCallback((p: typeof properties[number]) => {
    // Prefer per-property ui schema if provided by RJSF
    const uis = (p as any)?.uiSchema ?? {}
    const fieldOverride = uis["ui:field"]
    const widgetOverride = uis["ui:widget"]
    const tabPref = uis?.["ui:options"]?.tab as "general" | "tab" | undefined

    // If the integrator says so, obey explicitly
    if (tabPref === "general") return true
    if (tabPref === "tab") return false

    // If there's an override (custom field or widget), default to "leaf-like"
    if (fieldOverride || widgetOverride) return true

    // Otherwise, fall back to the actual JSON schema type
    const type = p?.content?.props?.schema?.type
    return type !== "object" && type !== "array"
  }, [])

  // Split once, and filter by visibility (now honoring ui:field/ui:widget)
  const { visibleNonObject, visibleObject } = React.useMemo(() => {
    const nonObj: typeof properties = []
    const obj: typeof properties = []
    for (const p of properties) {
      if (!isVisible(p.name)) continue
      if (p.name === "goalSettings" && !isGoalRaffleFormData(formData)) continue
      if (isLeafLike(p)) nonObj.push(p)
      else obj.push(p)
    }
    return { visibleNonObject: nonObj, visibleObject: obj }
  }, [properties, isLeafLike, isVisible, formData])

  // Fields sharing the same ui:options.group render together inside a bordered section
  // (in the General tab) instead of each getting its own standalone row.
  const groupFor = React.useCallback(
    (p: typeof properties[number]) => (p as any)?.uiSchema?.["ui:options"]?.group as string | undefined,
    [],
  )

  const generalSections = React.useMemo(() => {
    const sections: Array<{ key: string; group?: string; items: typeof visibleNonObject }> = []
    const groupIndex = new Map<string, number>()
    for (const p of visibleNonObject) {
      const group = groupFor(p)
      if (!group) {
        sections.push({ key: p.name, items: [p] })
        continue
      }
      const existingIndex = groupIndex.get(group)
      if (existingIndex === undefined) {
        groupIndex.set(group, sections.length)
        sections.push({ key: `group:${group}`, group, items: [p] })
      } else {
        sections[existingIndex].items.push(p)
      }
    }
    return sections
  }, [visibleNonObject, groupFor])

  // Prefer a friendly tab label: property title if available
  const tabLabelFor = React.useCallback(
    (p: typeof properties[number]) => p?.content?.props?.schema?.title ?? p.name,
    [],
  )

  const tabs = React.useMemo(
    () => [
      { key: "__general__", label: generalLabel },
      ...visibleObject.map((p) => ({ key: p.name, label: tabLabelFor(p) })),
    ],
    [generalLabel, visibleObject, tabLabelFor],
  )

  React.useEffect(() => {
    if (value >= tabs.length) setValue(Math.max(0, tabs.length - 1))
  }, [tabs.length, value])

  const tabA11yProps = React.useCallback(
    (index: number) => ({
      id: `${idBase}-tab-${index}`,
      "aria-controls": `${idBase}-tabpanel-${index}`,
    }),
    [idBase],
  )

  const isScrollable = tabVariant === "scrollable"

  return (
    <Box sx={CONTAINER_SX}>
      {(title || description) && (
        <Box sx={HEADER_SX}>
          {title && (
            <Typography variant="h6" gutterBottom>
              {title} {required ? "*" : ""}
            </Typography>
          )}
          {description && (
            <Typography variant="body2" color="text.secondary">
              {description}
            </Typography>
          )}
        </Box>
      )}

      {visibilityError && (
        <Box sx={STATUS_SX}>
          <Alert severity="warning" variant="outlined">
            {errorLabel}
          </Alert>
        </Box>
      )}

      <Tabs
        value={value}
        onChange={onChange}
        aria-label={title ? `${title} fields` : "Object properties tabs"}
        variant={tabVariant}
        scrollButtons={isScrollable ? "auto" : false}
        allowScrollButtonsMobile={isScrollable}
        sx={TABS_SX}
      >
        {tabs.map((t, i) => (
          <Tab key={t.key} label={t.label} disabled={isLoading} {...tabA11yProps(i)} />
        ))}
      </Tabs>

      {/* General (leaf-like fields, including those with ui:widget/ui:field) */}
      <TabPanel value={value} index={0} idBase={idBase}>
        {isLoading ? (
          <LoadingState message={loadingLabel} />
        ) : generalSections.length > 0 ? (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
            {generalSections.map((section) =>
              section.group ? (
                <Box key={section.key} sx={GROUP_SX}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    {section.group}
                  </Typography>
                  <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
                    {section.items.map((p) => (
                      <Box key={p.name}>{p.content}</Box>
                    ))}
                  </Box>
                </Box>
              ) : (
                <Box key={section.key}>{section.items[0].content}</Box>
              ),
            )}
          </Box>
        ) : (
          <Typography variant="body2" color="text.secondary">
            {emptyStateLabel}
          </Typography>
        )}
      </TabPanel>

      {/* True object/array fields (without overrides) get their own tab */}
      {visibleObject.map((p, idx) => (
        <TabPanel key={p.name} value={value} index={idx + 1} idBase={idBase}>
          {isLoading ? (
            <LoadingState message={loadingLabel} />
          ) : (
            <LocationProvider formData={(formData as any)?.[p.name]}>
              {p.content}
            </LocationProvider>
          )}
        </TabPanel>
      ))}

      {title === "Ticket Design" && !isLoading && (
        <Box sx={{ ...PANEL_SX, pt: 0 }}>
          <TicketPreview design={formData || {}} />
        </Box>
      )}
    </Box>
  )
}

export default CustomObjectFieldTabs
